import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, FileBarChart, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const LoanEmiReport = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | closed | all

  useEffect(() => { fetchData(); }, [selectedBranch, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBranch !== 'all') params.branch_id = selectedBranch;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [loanResp, branchResp] = await Promise.all([api.get('/loans', { params }), api.get('/branches')]);
      setLoans(loanResp.data);
      setBranches(branchResp.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => selectedBranch === 'all' ? 'All Branches' : branches.find(b => b.id === selectedBranch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!loans.length) { toast.error('No loans to export'); return; }
    const rows = loans.map((l, i) => ({
      'SN': i + 1,
      'Loan ID': l.loan_id,
      'Entrepreneur': l.entrepreneur_name,
      'Type': l.loan_type,
      'Disbursement Date': l.disbursement_date ? new Date(l.disbursement_date).toLocaleDateString('en-IN') : '',
      'Loan Amount': Math.round(l.loan_amount || 0),
      'Interest Rate (%)': l.interest_rate || 0,
      'Tenure (months)': l.tenure_months || 0,
      'Monthly EMI': Math.round(l.emi_amount || 0),
      'Outstanding': Math.round(l.outstanding_amount || 0),
      'Status': l.status || '',
    }));
    exportToExcel({
      filename: `WWAGS_Loan_EMI_${getBranchName().replace(/\s+/g,'_')}`,
      sheetName: 'Loan EMI Report',
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Total Loans', value: loans.length },
        { label: 'Total Disbursed', value: Math.round(loans.reduce((s,l) => s + (l.loan_amount || 0), 0)) },
        { label: 'Total Outstanding', value: Math.round(loans.reduce((s,l) => s + (l.outstanding_amount || 0), 0)) },
      ],
    });
  };

  const handlePrint = () => {
    const rows = loans.map((l, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${l.loan_id}</td><td>${l.entrepreneur_name}</td><td>${l.loan_type}</td><td>${l.disbursement_date?new Date(l.disbursement_date).toLocaleDateString('en-IN'):'-'}</td><td style="text-align:right">Rs.${(l.loan_amount||0).toLocaleString('en-IN')}</td><td style="text-align:center">${l.interest_rate}%</td><td style="text-align:center">${l.tenure_months} months</td><td style="text-align:right">Rs.${(l.emi_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(l.outstanding_amount||0).toLocaleString('en-IN')}</td></tr>`).join('');
    const body = `<table><thead><tr><th>Loan ID</th><th>Entrepreneur</th><th>Type</th><th>Disbursement Date</th><th>Amount</th><th>Interest Rate</th><th>Tenure</th><th>Monthly EMI</th><th>Outstanding</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('Loan EMI Report', body, getBranchName()));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-700"></div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="loan-emi-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1"><h1 className="text-2xl font-bold text-slate-900">Loan EMI Report</h1><p className="text-sm text-slate-600">Summary of all loan EMI schedules</p></div>
        <div className="flex items-center gap-2">
          {isAdmin() && (
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32" data-testid="loan-emi-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-3"><p className="text-xs text-purple-700 mb-1">Total Disbursed</p><p className="text-xl font-bold text-purple-900">Rs.{loans.reduce((s,l) => s + (l.loan_amount || 0), 0).toLocaleString('en-IN')}</p></div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3"><p className="text-xs text-emerald-700 mb-1">Total Loans</p><p className="text-xl font-bold text-emerald-900">{loans.length}</p></div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-3"><p className="text-xs text-red-700 mb-1">Outstanding</p><p className="text-xl font-bold text-red-900">Rs.{loans.reduce((s,l) => s + (l.outstanding_amount || 0), 0).toLocaleString('en-IN')}</p></div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-purple-700 text-white">
            <tr>
              <th className="px-3 py-2 text-left">Loan ID</th>
              <th className="px-3 py-2 text-left">Entrepreneur</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Disbursement</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-center">Rate</th>
              <th className="px-3 py-2 text-center">Tenure</th>
              <th className="px-3 py-2 text-right">EMI</th>
              <th className="px-3 py-2 text-right">Outstanding</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loans.length === 0 ? <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-500">No loans found</td></tr> : loans.map((loan, i) => (
              <tr key={loan.loan_id} className={`${i % 2 === 0 ? '' : 'bg-slate-50'} hover:bg-slate-100`}>
                <td className="px-3 py-2"><Link to={`/loans/${loan.id}`} className="text-blue-700 hover:underline font-medium">{loan.loan_id}</Link></td>
                <td className="px-3 py-2">{loan.entrepreneur_name}</td>
                <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{loan.loan_type}</span></td>
                <td className="px-3 py-2 text-slate-600">{loan.disbursement_date ? new Date(loan.disbursement_date).toLocaleDateString('en-IN') : '-'}</td>
                <td className="px-3 py-2 text-right font-medium">Rs.{(loan.loan_amount||0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-center">{loan.interest_rate}%</td>
                <td className="px-3 py-2 text-center">{loan.tenure_months}m</td>
                <td className="px-3 py-2 text-right">Rs.{(loan.emi_amount||0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right text-red-600 font-medium">Rs.{(loan.outstanding_amount||0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${loan.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{loan.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LoanEmiReport;
